import { LottieWrapperType } from "@components/RedesignComponents/types/lottieWrapperTypes";
import React, { useState, useEffect, useRef } from "react";
import { Player } from "@lottiefiles/react-lottie-player";
import { Builder } from "@builder.io/react";

const LottieWrapper: React.FC<LottieWrapperType> = ({
  lottieJson,
  posterImage,
  fallbackImage,
  loop,
  speed,
  playWhenVisible = false,
  threshold = "0.5",
  delay = "0",
}) => {
  const [posterImageDisplay, setPosterImageDisplay] = useState(true);
  const [fallbackImageDisplay, setFallbackImageDisplay] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const lottieDiv = useRef(null);
  const playerRef = useRef<Player>(null);
  // Set a minimum delay so the animation can load after mounting.
  const animationDelay = Number(delay) > 500 ? Number(delay) : 500;

  useEffect(() => {
    if (!playWhenVisible) {
      setTimeout(() => {
        if (playerRef.current) {
          playerRef.current.play();
        }
      }, animationDelay);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: Number(threshold) }
    );

    // We need a set timeout for the lottie to load
    // after mounting for animations above the fold.
    setTimeout(() => {
      if (lottieDiv.current) {
        observer.observe(lottieDiv.current);
      }
    }, 350);

    return () => observer.disconnect();
  }, [playWhenVisible, isVisible]);

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        if (playerRef.current) {
          playerRef.current.play();
        }
      }, animationDelay);
    }
  }, [isVisible]);

  return (
    <div className="container">
      {posterImageDisplay && (
        <img src={posterImage?.image} alt={posterImage?.imageAltText} />
      )}
      {fallbackImageDisplay && (
        <img src={fallbackImage?.image} alt={fallbackImage?.imageAltText} />
      )}
      <div ref={lottieDiv}>
        <Player
          ref={playerRef}
          src={lottieJson}
          className="player flex"
          renderer="svg"
          loop={loop}
          speed={speed}
          autoplay={false}
          keepLastFrame={true}
          onEvent={event => {
            if (event === "load") {
              setPosterImageDisplay(false);
            } else if (event === "error") {
              setPosterImageDisplay(false);
              setFallbackImageDisplay(true);
            }
          }}
        />
      </div>
    </div>
  );
};

export default LottieWrapper;

export const RegisterLottie = () => {
  Builder.registerComponent(LottieWrapper, {
    name: "lottie",
    inputs: [
      {
        name: "lottieJson",
        required: true,
        type: "file",
        friendlyName: "Lottie animation in JSON",
        defaultValue:
          "https://assets1.lottiefiles.com/packages/lf20_myejiggj.json",
      },
      {
        name: "posterImage",
        type: "object",
        subFields: [
          {
            name: "image",
            type: "file",
          },
          {
            name: "imageAltText",
            type: "longText",
          },
        ],
        helperText: "The image that displays while the Lottie is loading",
      },
      {
        name: "fallbackImage",
        type: "object",
        subFields: [
          {
            name: "image",
            type: "file",
          },
          {
            name: "imageAltText",
            type: "longText",
          },
        ],
        helperText:
          "The image that displays if there is an error in loading the Lottie",
      },
      {
        name: "loop",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "playWhenVisible",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "threshold",
        type: "string",
        defaultValue: "0.5",
        helperText:
          "Used with Play When Visible, this controls how much of the animation must be visible for it to play. The value *must* be between 0.1 and 1.0, and 1.0 means that 100% of the Lottie has to be visible to play.",
      },
      {
        name: "delay",
        type: "string",
        defaultValue: "0",
        helperText:
          "This adds a delay to when the animation will start. The delay is measured in milliseconds (1000 = 1 second).",
      },
      {
        name: "speed",
        type: "number",
        defaultValue: 1,
      },
    ],
  });
};
